import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { parse } from '../../../../util/urlHelpers';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import IconBookMark from '../../../../containers/WishlistPage/IconBookMark';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.buttonLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const SellNowLink = () => {
  return (
    <NamedLink name="NewListingPage" className={css.sellNowLink}>
      <span className={css.buttonLabel}>
        <FormattedMessage id="TopbarDesktop.sellNow" defaultMessage="Sell now" />
      </span>
    </NamedLink>
  );
};

const HeartLink = () => {
  return (
    <NamedLink name="WishlistPage" className={css.heartLink}>
      <div className={css.heartIconContainer}>
        <IconBookMark liked={false} className={css.heartIcon} />
      </div>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({
  notificationCount,
  salesNotificationCount,
  ordersNotificationCount,
  currentPage,
}) => {
  const isInboxPage = currentPage?.indexOf('InboxPage') === 0;
  const currentTab = isInboxPage ? currentPage.split(':')[1] : null;

  let showDot = false;
  if (isInboxPage) {
    if (currentTab === 'sales') {
      showDot = salesNotificationCount > 0;
    } else if (currentTab === 'orders') {
      showDot = ordersNotificationCount > 0;
    }
  } else {
    showDot = notificationCount > 0;
  }

  const notificationDot = showDot ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="DashboardPage">
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="WishlistPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('WishlistPage'))}
            name="WishlistPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourWishlistLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    salesNotificationCount = 0,
    ordersNotificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
  } = props;
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const queryParams = parse(location.search);

  const isCategoryActive = categoryValue => {
    const activeCategory = queryParams.pub_level2;
    return currentPage === 'SearchPage' && activeCategory === categoryValue;
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      salesNotificationCount={salesNotificationCount}
      ordersNotificationCount={ordersNotificationCount}
      currentPage={currentPage}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const sellNowLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SellNowLink />;
  const heartLinkMaybe = <HeartLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <div className={css.topbarWrapper}>
      <nav className={classes}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {searchFormMaybe}

        {heartLinkMaybe}
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        />
        {sellNowLinkMaybe}

        {inboxLinkMaybe}
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </nav>
      <div className={css.categoryLinksRow}>
        {[
          { id: 'baseballcards', value: 'baseballcards,rawbaseball', labelId: 'TopbarMobileMenu.categoryBaseball' },
          { id: 'basketballcards', value: 'basketballcards,rawbasketball', labelId: 'TopbarMobileMenu.categoryBasketball' },
          { id: 'footballcards', value: 'footballcards,rawfootball', labelId: 'TopbarMobileMenu.categoryFootball' },
          { id: 'soccercards', value: 'soccercards,rawsoccer', labelId: 'TopbarMobileMenu.categorySoccer' },
          { id: 'hockeycards', value: 'hockeycards,rawhockey', labelId: 'TopbarMobileMenu.categoryHockey' },
          { id: 'wrestlingcards', value: 'wrestlingcards,rawwrestling', labelId: 'TopbarMobileMenu.categoryWrestling' },
          { id: 'misccards', value: 'misccards,rawmisccards', labelId: 'TopbarMobileMenu.categoryMisc' },
        ].map(cat => (
          <NamedLink
            key={cat.id}
            name="SearchPage"
            to={{ search: `?pub_level2=${cat.value}` }}
            className={classNames(css.categoryLink, isCategoryActive(cat.value) ? css.categoryLinkActive : null)}
          >
            <FormattedMessage id={cat.labelId} />
          </NamedLink>
        ))}
        <NamedLink
          name="SearchPage"
          className={classNames(css.categoryLink, currentPage === 'SearchPage' && !queryParams.pub_level2 ? css.categoryLinkActive : null)}
        >
          <FormattedMessage id="TopbarMobileMenu.allListingsLink" />
        </NamedLink>
      </div>
    </div>
  );
};

export default TopbarDesktop;
